'use client';

import { useLocale } from 'next-intl';
import { useMemo } from 'react';

import { getScheduledPageCopy } from '@/app/[orgSlug]/scheduled/_copy/scheduled-page-copy';
import {
  arrangeScheduleList,
  formatRelativeFromNow,
} from '@/app/[orgSlug]/scheduled/_lib/scheduled-list-state';
import { useMinuteClock } from '@/components/notifications/use-minute-clock';
import { api, useQuery } from '@/lib/convex';
import type { ScheduleResult } from '../types';

const SCHEDULE_LIMIT = 15;

/**
 * The Scheduled scope: schedules the viewer can read, filtered by title as
 * they type and ordered by next run. The list is bounded server-side, so
 * filtering happens here without a search index.
 */
export function useScheduleSearch({
  active,
  orgSlug,
  term,
}: {
  active: boolean;
  orgSlug?: string;
  term: string;
}): { results: ScheduleResult[]; isPending: boolean } {
  const locale = useLocale();
  const now = useMinuteClock();
  const organization = useQuery(
    api.organizations.queries.getBySlug,
    active && orgSlug ? { slug: orgSlug } : 'skip'
  );
  const organizationId = organization.data?._id;
  const list = useQuery(
    api.agent_schedules.lifecycle.listSchedules,
    active && organizationId
      ? { organizationId, statuses: ['active', 'paused', 'completed'] }
      : 'skip'
  );

  const results = useMemo(() => {
    if (!active || !list.data) return [];
    const statusLabels = getScheduledPageCopy(locale).filters;
    return arrangeScheduleList(list.data.items, {
      search: term,
      sort: 'nextRun',
      locale,
    })
      .slice(0, SCHEDULE_LIMIT)
      .map((item) => ({
        _id: item.scheduleId,
        title: item.title,
        hint:
          item.status === 'active' && item.nextRunAt !== undefined
            ? formatRelativeFromNow(item.nextRunAt, now, locale)
            : statusLabels[
                item.status === 'archived' ? 'archived' : item.status
              ],
      }));
  }, [active, list.data, locale, now, term]);
  const isPending =
    active &&
    Boolean(orgSlug) &&
    (organization.data === undefined ||
      (organizationId !== undefined && list.data === undefined));
  return { results, isPending };
}
